/* THE COMMAND LINE. A short world mnemonic and a subject, Enter to execute: `BED 7`, `RUN 2 ANALYSIS`, `CMP v3 v4`.
   Typing finds a mnemonic by its first letters or by a word of what it does, so none has to be remembered. Run numbers
   are the order the runs were opened, oldest first, so RUN 2 is the same run for ever. Pure: text and context in, a
   destination or a refusal out. */
import { comparable, hasAnalysis, STATUS_TEXT } from './runs.ts'
import type { Run } from '../types.ts'
import { partyIndex, type PartyRef } from './office.ts'

export type CommandCtx = { beds: number; runs: readonly Run[]; program: boolean; parties?: readonly PartyRef[] }
export type Outcome = { kind: 'go'; to: string; said: string | null } | { kind: 'act'; act: 'dark' | 'light' | 'signout' } | { kind: 'refuse'; message: string }
export type Mnemonic = { word: string; usage: string; gist: string; programOnly?: boolean }

/** Every mnemonic, in one fixed order. */
export const MNEMONICS: Mnemonic[] = [
  { word: 'WORLD', usage: 'WORLD 001', gist: 'World 001, live' },
  { word: 'WORLD', usage: 'WORLD', gist: 'what the world holds' },
  { word: 'BED', usage: 'BED 7', gist: 'open bed 7' },
  { word: 'PLAY', usage: 'PLAY', gist: 'play a sample case yourself' },
  { word: 'RUNS', usage: 'RUNS', gist: 'every run, newest first' },
  { word: 'RUN', usage: 'RUN 2', gist: 'the second run you opened' },
  { word: 'RUN', usage: 'RUN 2 ANALYSIS', gist: 'its full analysis' },
  { word: 'NEW', usage: 'NEW EVALUATION', gist: 'open a new run' },
  { word: 'CMP', usage: 'CMP v3 v4', gist: 'compare two versions' },
  { word: 'RESIDENTS', usage: 'RESIDENTS', gist: 'your Residents and their versions' },
  { word: 'CONNECT', usage: 'CONNECT', gist: 'how a Resident connects' },
  { word: 'LICENSE', usage: 'LICENSE', gist: 'the license and its meters' },
  { word: 'ACCOUNT', usage: 'ACCOUNT', gist: 'tokens and the console session' },
  { word: 'PROGRAM', usage: 'PROGRAM', gist: 'invitations and verdicts', programOnly: true },
  { word: 'PARTY', usage: 'PARTY 2', gist: 'the second party you invited, in the order of invitation', programOnly: true },
  { word: 'VERDICT', usage: 'VERDICT 2', gist: 'the verdict document of the second party’s latest version', programOnly: true },
  { word: 'HOSTS', usage: 'HOSTS', gist: 'runs a robot host played' },
  { word: 'DARK', usage: 'DARK', gist: 'dark theme' },
  { word: 'LIGHT', usage: 'LIGHT', gist: 'light theme' },
  { word: 'SIGNOUT', usage: 'SIGNOUT', gist: 'end the console session' },
]

const go = (to: string, said: string | null = null): Outcome => ({ kind: 'go', to, said })
const refuse = (message: string): Outcome => ({ kind: 'refuse', message })

/** Run numbers: the order the runs were opened, oldest first, so a number names one run for ever. */
export function runNumbers(runs: readonly Run[]): Map<string, number> {
  const ordered = [...runs].sort((a, b) => a.created.localeCompare(b.created) || a.id.localeCompare(b.id))
  return new Map(ordered.map((r, i) => [r.id, i + 1]))
}

function runByNumber(token: string, runs: readonly Run[]): Run | string {
  const byId = runs.find((r) => r.id === token || r.id.toLowerCase() === token.toLowerCase())
  if (byId) return byId
  if (!/^\d+$/.test(token)) return `${token.slice(0, 40)} is not a run number or a run id.`
  const n = Number(token)
  for (const [id, k] of runNumbers(runs)) if (k === n) return runs.find((r) => r.id === id) as Run
  return runs.length === 0 ? 'This account has opened no run yet.' : `There is no run ${n}: this account has opened ${runs.length}.`
}

/** A comparison side: `v3` (the newest complete evaluation run of version 3), `name@3`, or `r2` / `2` (run 2). */
function side(token: string, runs: readonly Run[]): Run | string {
  const t = token.trim()
  const ver = /^v(.+)$/i.exec(t)
  const at = /^(.+)@(.+)$/.exec(t)
  if (!ver && !at) {
    const r = runByNumber(t.replace(/^r/i, ''), runs)
    return typeof r === 'string' ? r : r.kind === 'evaluation' && r.status === 'complete' ? r : `Run ${t.replace(/^r/i, '')} is not a complete evaluation run.`
  }
  const want = at ? `${at[1]}@${at[2]}`.toLowerCase() : null
  const version = ver ? ver[1].toLowerCase() : null
  const have = comparable(runs).filter((r) => r.agent && (want ? r.agent.toLowerCase() === want : r.agent.toLowerCase().endsWith(`@${version}`)))
  if (have.length === 0) return `No complete evaluation run of version ${at ? t : ver?.[1]}. A comparison needs one for each side.`
  const names = [...new Set(have.map((r) => r.agent as string))]
  if (names.length > 1) return `More than one version is called ${t}: ${names.join(', ')}. Say which, for example CMP ${names[0]} ${names[1]}.`
  return have[0]
}

/** Executes a line. */
export function resolve(input: string, ctx: CommandCtx): Outcome {
  const tokens = input.trim().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return refuse('Type a mnemonic, for example BED 7. The list below shows them all.')
  const word = tokens[0].toUpperCase()
  const rest = tokens.slice(1)
  switch (word) {
    case 'WARD':
      return go('/')
    case 'WORLD':
      return go('/world')
    case 'PLAY':
    case 'BEDSIDE':
      return go('/bedside')
    case 'BED': {
      if (ctx.beds === 0) return refuse('The world publishes no bay table, so the world has no beds to open.')
      if (rest.length !== 1 || !/^\d+$/.test(rest[0])) return refuse(`Name the bed by its number, from 1 to ${ctx.beds}, for example BED 7.`)
      const n = Number(rest[0])
      return n >= 1 && n <= ctx.beds ? go(`/bed/${n}`) : refuse(`There is no bed ${n}: the world has ${ctx.beds}, numbered 1 to ${ctx.beds}.`)
    }
    case 'RUNS':
      return go('/runs')
    case 'RUN': {
      if (rest.length === 0) return go('/runs')
      const r = runByNumber(rest[0], ctx.runs)
      if (typeof r === 'string') return refuse(r)
      if (rest.length === 1) return go(`/runs/${r.id}`)
      if (rest.length === 2 && rest[1].toUpperCase() === 'ANALYSIS') return hasAnalysis(r.status) ? go(`/runs/${r.id}#analysis`) : go(`/runs/${r.id}`, `${r.id} is ${STATUS_TEXT[r.status].toLowerCase()}; its run analysis opens when it has been scored.`)
      return refuse('After the run, say ANALYSIS or nothing, for example RUN 2 ANALYSIS.')
    }
    case 'NEW': {
      const kind = (rest[0] ?? '').toLowerCase()
      if (!rest.length) return go('/runs/new')
      return kind === 'sample' || kind === 'train' || kind === 'evaluation' ? go(`/runs/new?kind=${kind}`) : refuse('A run is a sample, a train or an evaluation run, for example NEW EVALUATION.')
    }
    case 'CMP': {
      if (rest.length === 0) return go('/compare')
      if (rest.length !== 2) return refuse('Name two sides, for example CMP v3 v4.')
      const [a, b] = rest.map((t) => side(t, ctx.runs))
      if (typeof a === 'string') return refuse(a)
      if (typeof b === 'string') return refuse(b)
      if (a.id === b.id) return refuse(`Both sides are ${a.id}. Name two different versions.`)
      return go(`/compare?a=${encodeURIComponent(a.id)}&b=${encodeURIComponent(b.id)}`)
    }
    case 'RESIDENTS':
    case 'AGENTS':
      return go('/agents')
    case 'CONNECT':
      return go('/connect')
    case 'LICENSE':
    case 'LICENCE':
      return go('/license')
    case 'ACCOUNT':
      return go('/account')
    case 'PROGRAM':
      return ctx.program ? go('/program') : refuse('This account is not a program, so it has no invitations to make.')
    case 'PARTY':
    case 'VERDICT': {
      if (!ctx.program) return refuse('This account is not a program, so it has invited no party.')
      const parties = ctx.parties ?? []
      if (rest.length !== 1) return refuse(`Name the party by its place in the order you invited them, or by its name, for example ${word} 2.`)
      const n = partyIndex(rest[0], parties)
      if (typeof n === 'string') return refuse(n)
      if (word === 'PARTY') return go(`/program#party-${n}`)
      const runs = parties[n - 1].runs
      return runs.length ? go(`/verdict/${runs[runs.length - 1]}`) : refuse(`${parties[n - 1].name} has no scored evaluation run yet, so there is no verdict to open.`)
    }
    case 'HOSTS':
    case 'BENCH': {
      if (rest.length === 0) return go('/bench')
      const r = runByNumber(rest[0], ctx.runs)
      if (typeof r === 'string') return refuse(r)
      return r.residents === 'remote' ? go(`/bench/${r.id}`) : refuse(`${r.id} was not played by a host, so it has no stations. A bench is for a run opened with residents: remote.`)
    }
    case 'DARK':
      return { kind: 'act', act: 'dark' }
    case 'LIGHT':
      return { kind: 'act', act: 'light' }
    case 'SIGNOUT':
      return { kind: 'act', act: 'signout' }
  }
  return refuse(`${tokens[0].slice(0, 40)} is not a mnemonic. Type a letter to see those that begin with it.`)
}

/** The mnemonics a half-typed line could mean, in the fixed order: those whose word begins with the first word typed, and those whose gist contains it. All of them for an empty line. */
export function suggest(input: string, ctx: Pick<CommandCtx, 'program'>): Mnemonic[] {
  const first = input.trim().split(/\s+/)[0]?.toLowerCase() ?? ''
  const open = MNEMONICS.filter((m) => !m.programOnly || ctx.program)
  if (!first) return open
  return open.filter((m) => m.word.toLowerCase().startsWith(first) || m.usage.toLowerCase().startsWith(first) || m.gist.toLowerCase().includes(first))
}
